import React, { useState } from 'react';
import { User, Search, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StudentLogin = ({ onStudentLogin }) => {
  const [imsPin, setImsPin] = useState('');
  const [category, setCategory] = useState('General'); // Default; MongoDB is source of truth after login
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!imsPin.trim()) {
      setError('Please enter your IMS PIN');
      return;
    }

    // Use the exact IMS PIN as entered by the user - no manipulation
    const userEnteredPin = imsPin.trim();
    
    console.log(`🔍 StudentLogin: Checking student PIN: "${userEnteredPin}"`);

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // OPTIMIZATION 1: Single API call to get both existence and tests
      // This reduces round-trip time from 2 sequential calls to 1
      const testsResponse = await axios.get(`${API_BASE_URL}/api/student/${userEnteredPin}/tests`);
      
      if (!testsResponse.data || testsResponse.data.length === 0) {
        setError('No tests found for this student PIN. Please check your PIN and try again.');
        setLoading(false);
        return;
      }

      setSuccess('Login successful! Loading your dashboard...');
      
      // Category lives in MongoDB only — no localStorage duplication
      // Save category to MongoDB so it persists across sessions
      try {
        await axios.post(`${API_BASE_URL}/api/student/${userEnteredPin}/category`, {
          category: category
        });
      } catch (catErr) {
        console.warn('⚠️ Could not save category to DB:', catErr.message);
      }

      // OPTIMIZATION 2: Remove artificial 1-second delay
      // Call parent immediately for faster UX
      onStudentLogin({
        imsPin: userEnteredPin, // Use exact PIN as entered
        tests: testsResponse.data,
        category: category // Pass selected category
      });

    } catch (error) {
      console.error('Login error:', error);
      
      // Better error handling for different scenarios
      if (error.response?.status === 404) {
        setError('Student PIN not found. Please check your PIN and try again.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again in a moment.');
      } else {
        setError(error.response?.data?.error || 'Failed to login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="bg-indigo-100 rounded-full p-4 inline-block mb-4">
            <User className="text-indigo-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Student Analytics Portal</h1>
          <p className="text-gray-600">Enter your IMS PIN to access your test analytics</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="imsPin" className="block text-sm font-medium text-gray-700 mb-2">
              IMS PIN
            </label>
            <div className="relative">
              <input
                type="text"
                id="imsPin"
                value={imsPin}
                onChange={(e) => setImsPin(e.target.value)}
                placeholder="Enter your IMS PIN"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                disabled={loading}
              />
              <Search className="absolute right-3 top-3 text-gray-400" size={20} />
            </div>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white"
              disabled={loading}
            >
              <option value="General">General (UR)</option>
              <option value="EWS">EWS (Economically Weaker Section)</option>
              <option value="NC-OBC">NC-OBC (Non-Creamy Layer OBC)</option>
              <option value="SC">SC (Scheduled Caste)</option>
              <option value="ST">ST (Scheduled Tribe)</option>
              <option value="PWD">PWD (Persons with Disabilities)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Select your category to see personalized cutoffs and recommendations
            </p>
          </div>

          {error && (
            <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircle size={20} />
              <span className="text-sm">{success}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Authenticating...</span>
              </div>
            ) : (
              'Access Analytics'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secure access to your IIMB UG AT test performance analytics
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;